import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/*
  Zadanie: maksymalne pole wybiegu przy ścianie (ogrodzenie z 3 stron).
  Losujemy tylko długość siatki L (w metrach) — najlepiej wielokrotność 4,
  żeby wyniki były całkowite.

  Warunek: 2a + b = L
  Pole: P = a*b = a(L - 2a) = -2a^2 + La
  Maksimum: a = L/4, b = L/2, Pmax = L^2/8

  Punktacja: 1 pkt (tylko Pmax).
*/

/* =========================
   Helpers
========================= */

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function parseNumber(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (raw === "") return null;

  const frac = raw.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!Number.isFinite(p) || !Number.isFinite(q) || q === 0) return null;
    return p / q;
  }

  if (/^[+\-]?\d+(\.\d+)?$/.test(raw)) {
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

/* =========================
   Diagram (statyczny, bez podpisów x/y)
========================= */

function FenceAgainstWallDiagram() {
  return (
    <svg
      viewBox="0 0 520 220"
      width="100%"
      style={{ maxWidth: 520, display: "block" }}
    >
      {/* ŚCIANA: stonowany pomarańcz */}
      <rect
        x="70"
        y="35"
        width="380"
        height="26"
        rx="6"
        fill="rgba(255, 153, 51, 0.55)"
      />

      {/* Ogrodzenie: bierze kolor z motywu (currentColor) */}
      <g stroke="currentColor" strokeWidth="4">
        <line x1="120" y1="60" x2="120" y2="170" />
        <line x1="400" y1="60" x2="400" y2="170" />
        <line x1="120" y1="170" x2="400" y2="170" />
      </g>

      {/* Markery wymiarów (bez liter) */}
      <g stroke="currentColor" strokeWidth="2" opacity="0.55">
        {/* pionowy */}
        <line x1="108" y1="70" x2="108" y2="160" />
        <line x1="103" y1="75" x2="108" y2="70" />
        <line x1="113" y1="75" x2="108" y2="70" />
        <line x1="103" y1="155" x2="108" y2="160" />
        <line x1="113" y1="155" x2="108" y2="160" />

        {/* poziomy */}
        <line x1="130" y1="188" x2="390" y2="188" />
        <line x1="135" y1="183" x2="130" y2="188" />
        <line x1="135" y1="193" x2="130" y2="188" />
        <line x1="385" y1="183" x2="390" y2="188" />
        <line x1="385" y1="193" x2="390" y2="188" />
      </g>
    </svg>
  );
}

/* =========================
   Generator
========================= */

export function buildThreeSideRectangleData(L){return {L,aOpt:L/4,bOpt:L/2,Pmax:L*L/8};}
export function generateTask() {
  const L = pick([12, 16, 20, 24, 28, 32]);

  return {
    type: "FenceMaxAreaAgainstWallTask",
    data: {
      ...buildThreeSideRectangleData(L),
      maxPoints: 1,
    },
  };
}

/* =========================
   Render task
========================= */

export function renderTask({ data }) {
  const { L } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p><strong>Zadanie optymalizacyjne.</strong></p>

      <p>
        Gospodarz ma siatkę o długości <InlineMath math={`${L}`} /> m, aby ogrodzić
        prostokątny wybieg dla psa przylegający jednym bokiem do budynku
        (budynek zastępuje jeden bok ogrodzenia).
      </p>

      <div style={{ margin: "10px 0 6px 0", color: "inherit" }}>
        <FenceAgainstWallDiagram />
      </div>

      <p>
        Oblicz pole powierzchni <strong>największego</strong> takiego wybiegu.
      </p>
    </div>
  );
}

/* =========================
   Input
========================= */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};

  return (
    <div style={{ display: "grid", rowGap: 10, maxWidth: 380 }}>
      <div>
        Podaj największe pole <InlineMath math={'P_{\\max}'} />:
      </div>

      <FractionInput
        value={ua.P}
        onChange={(v) => onChange({ ...(ua || {}), P: v })}
        disabled={disabled}
        label={'P_{\\max}'}
        suffixLatex={'\\text{m}^2'}
      />
    </div>
  );
}

/* =========================
   Check
========================= */

export function checkAnswer({ data }, userAnswer) {
  const v = parseNumber(userAnswer?.P);
  if (v == null) return false;
  return Math.abs(v - data.Pmax) < 1e-9;
}

/* =========================
   Answer + User answer
========================= */

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <InlineMath math={`P_{\\max}=${data.Pmax}\\ \\text{m}^2`} />
    </div>
  );
}

export function renderUserAnswer(taskOrAnswer, maybeAnswer) {
  const answer = maybeAnswer ?? taskOrAnswer;
  if (!answer?.P) return <i>(brak odpowiedzi)</i>;

  const raw =
    typeof answer.P === "object" ? answer.P?.raw ?? "" : String(answer.P ?? "");
  const latex = raw.trim() ? raw.trim() : "\\text{–}";

  return <InlineMath math={`P_{\\max}=${latex}\\ \\text{m}^2`} />;
}

/* =========================
   Explanation (zgrabniej, bez x/y)
========================= */

export function renderExplanation({ data }) {
  const { L, aOpt, bOpt, Pmax } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1.</strong> Oznaczmy:
        {" "}
        <InlineMath math={"a"} /> – długość boku prostopadłego do budynku
        (takich boków są dwa),
        {" "}
        <InlineMath math={"b"} /> – długość boku równoległego do budynku
        (ten jeden ogrodzony bok).
      </div>

      <div style={{ marginBottom: 8 }}>
        Ponieważ ogrodzenie jest z trzech stron, długość siatki spełnia:
      </div>
      <BlockMath math={`2a+b=${L}.`} />
      <BlockMath math={`b=${L}-2a.`} />

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2.</strong> Pole wybiegu:
      </div>
      <BlockMath math={`P(a)=a\\cdot b=a(${L}-2a)=-2a^2+${L}a.`} />

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 3.</strong> To funkcja kwadratowa o ramionach w dół, więc maksimum jest w wierzchołku:
      </div>
      <BlockMath math={`a_w=-\\frac{b}{2a}=-\\frac{${L}}{2\\cdot(-2)}=\\frac{${L}}{4}=${aOpt}.`} />
      <BlockMath math={`b=${L}-2a_w=${L}-2\\cdot ${aOpt}=${bOpt}.`} />

      <div style={{ marginTop: 8 }}>
        <strong>Wniosek.</strong> Największe pole:
      </div>
      <BlockMath math={`P_{\\max}=${aOpt}\\cdot ${bOpt}=${Pmax}\\ \\text{m}^2.`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
